import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, CalendarDays, Building2, Clock, Plus, TrendingUp, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingsOverviewPage = () => {
  const navigate = useNavigate();
  const { hasCapability } = useAuth();
  const canCreateBookings = hasCapability('reservations.create');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics/dashboard`)
      .then((response) => setStats(response.data))
      .catch((error) => console.error('Failed to fetch booking overview:', error))
      .finally(() => setLoading(false));
  }, []);

  const formatBookingTime = (startTime) => {
    try {
      const date = typeof startTime === 'string' ? parseISO(startTime) : new Date(startTime);
      return format(date, 'EEE d MMM, HH:mm', { locale: nl });
    } catch {
      return 'Ongeldige datum';
    }
  };

  const openBooking = (bookingId) => navigate(bookingId ? `/bookings?bookingId=${bookingId}` : '/bookings');

  const statCards = [
    { title: 'Vandaag', value: stats?.today_bookings || 0, subtitle: 'boekingen', icon: CalendarDays, color: 'text-teal-600', bg: 'bg-teal-50' },
    { title: 'Totaal', value: stats?.total_bookings || 0, subtitle: 'boekingen', icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Resources', value: stats?.total_resources || 0, subtitle: 'actief', icon: Building2, color: 'text-purple-600', bg: 'bg-purple-50' },
    { title: 'Relaties', value: stats?.total_relations || 0, subtitle: 'geregistreerd', icon: User, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  if (loading) {
    return <DashboardLayout><div className="animate-pulse space-y-4"><div className="h-8 w-48 rounded bg-slate-200" /><div className="h-64 rounded-2xl bg-slate-200" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bookings-overview-page">
        <Breadcrumbs items={[{ label: 'Reserveringen', href: '/bookings/overview' }, { label: 'Overzicht' }]} />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Reserveringen</h1>
            <p className="text-slate-500">Overzicht van je boekingen en planning.</p>
          </div>
          {canCreateBookings && (
            <Button onClick={() => openBooking()} className="rounded-xl bg-teal-600 text-white hover:bg-teal-700" data-testid="new-booking-btn">
              <Plus className="mr-2 h-4 w-4" /> Nieuwe boeking
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-0 shadow-sm">
              <CardContent className="p-3 sm:p-5"><div className="flex items-start justify-between gap-2"><div><p className="text-xs text-slate-500 sm:text-sm">{stat.title}</p><p className="text-xl font-bold text-slate-900 sm:text-3xl">{stat.value}</p><p className="text-xs text-slate-400 sm:text-sm">{stat.subtitle}</p></div><div className={`rounded-xl p-2 sm:p-3 ${stat.bg}`}><stat.icon className={`h-4 w-4 sm:h-6 sm:w-6 ${stat.color}`} /></div></div></CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-lg font-semibold">Komende boekingen</CardTitle></CardHeader><CardContent>
            {stats?.upcoming_bookings?.length > 0 ? <div className="space-y-2">{stats.upcoming_bookings.slice(0, 5).map((booking, index) => <button key={booking.id || index} type="button" className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-slate-50" onClick={() => openBooking(booking.id)}><span className="h-10 w-1 rounded-full" style={{ backgroundColor: booking.resource_color || '#0d9488' }} /><span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-900">{booking.title}</span><span className="block truncate text-xs text-slate-500">{booking.resource_name}</span></span><span className="shrink-0 text-xs font-medium text-slate-700">{formatBookingTime(booking.start_time)}</span></button>)}</div> : <p className="py-6 text-center text-sm text-slate-500">Geen komende boekingen</p>}
          </CardContent></Card>
          <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-lg font-semibold">Recente activiteit</CardTitle></CardHeader><CardContent>
            {stats?.recent_activity?.length > 0 ? <div className="space-y-2">{stats.recent_activity.slice(0, 5).map((activity, index) => <button key={activity.id || index} type="button" className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-slate-50" onClick={() => openBooking(activity.id)}><span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100"><Clock className="h-4 w-4 text-slate-500" /></span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-900">{activity.title}</span><span className="block truncate text-xs text-slate-500">{activity.customer_name || 'Onbekende klant'} · {activity.resource_name}</span></span><span className="shrink-0 text-xs text-slate-500">{formatBookingTime(activity.start_time)}</span></button>)}</div> : <p className="py-6 text-center text-sm text-slate-500">Nog geen activiteit</p>}
          </CardContent></Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookingsOverviewPage;
